import Layout from 'components/layout';

import React, { type FC, useState, type PropsWithChildren } from 'react';

import { withImage, useSmartlingStrings } from '@lucid/hooks';
import { sendBeaconEvent, isDefined } from '@lucid/utils';
import {
  reactHTMLParser,
  Box,
  Container,
  Heading,
  Image,
  Link,
  Paper,
  PreFooterCta,
  Text,
  VideoPlayer,
  LucidHead,
  FAQSection,
  TemplateSection,
  SimpleCta,
} from '@lucid/components';
import { graphql, type HeadFC } from 'gatsby';

import type { Lucid } from 'types/types';
import SignUpCta from 'components/sign-up-cta/sign-up-cta';
import { useDefaultPageUrls } from 'hooks/hooks';

const Consideration: FC<PropsWithChildren<Lucid.Context.PageProps>> = ({ data, pageContext }) => {
  const { directus } = data;
  const [willPlayVideo, setWillPlayVideo] = useState(false);
  const { shortCode, urlLangPrefix, localeId, shouldHideSsr, pageType, nid, metatags, product } = pageContext;
  const withSmartling = useSmartlingStrings({
    keys: [
      'mktg_ct_consideration_header_scale',
      'mktg_ct_consideration_header_spark',
      'mktg_ct_consideration_video_cta',
      'mktg_nav_start_free_trial',
      'mktg_generics_sign_up_free',
      'mktg_learn_more',
      'mktg_consideration_see_why_spark',
      'mktg_abtest_hph1_heading_4',
    ],
    shortCode,
  });
  const node = directus.node;
  const translatedData = node.translations?.[0] as Lucid.Directus.Generated.Consideration_Translations;
  const { is_lead_gen, hero_image } = node;
  const {
    heading,
    feature_section_heading,
    feature_set,
    step_section_heading,
    steps,
    step_section_link_text,
    faq_section_heading,
    faqs,
    video,
    template_cards,
    template_section_heading,
    template_section_link,
    template_section_text,
    should_require_template_grid: shouldRequireTemplateGrid,
    pre_footer_heading,
  } = translatedData;

  const { nodeTranslations } = node;

  const { contact_sales_url } = useDefaultPageUrls(shortCode);

  const faqItems = faqs?.map(faq => faq.faq_id).filter(isDefined);
  const templateCards = template_cards?.map(card => card.template_card_id).filter(isDefined);
  const footerLink = contact_sales_url ? contact_sales_url : '/contact/contact-sales';

  return (
    <Layout
      localeId={localeId}
      metatags={metatags}
      nid={nid}
      nodeTranslations={nodeTranslations}
      pageType={pageType}
      product={product}
      shortCode={shortCode}
      shouldHideSsr={shouldHideSsr}
      urlLangPrefix={urlLangPrefix}
    >
      <Box
        css={{
          overflow: 'hidden',
        }}
      >
        <Container
          as="section"
          css={theme => ({
            display: 'flex',
            paddingBottom: theme.space.s104,
          })}
          extraPadding={3}
        >
          <Box
            css={{
              flex: '1',
            }}
          >
            <Heading
              as="h1"
              css={theme => ({
                marginTop: theme.space.s48,
                marginBottom: theme.space.s40,
              })}
              dataElementPageLocation="hero"
              variant="hero3XL700"
            >
              {heading}
            </Heading>
            <Text
              css={theme => ({
                marginTop: 0,
                marginBottom: theme.space.s40,
              })}
              variant="paragraphM400"
            >
              {product !== 'spark'
                ? withSmartling('mktg_ct_consideration_header_scale')
                : withSmartling('mktg_ct_consideration_header_spark') +
                  ' ' +
                  withSmartling('mktg_abtest_hph1_heading_4')}
            </Text>
            <Box
              css={theme => ({
                display: 'flex',
                flexWrap: 'wrap',
                gap: theme.space.s24,
                whiteSpace: 'nowrap',
                '> *': {
                  flex: '1 0 100%',
                  [theme.breakpoints.bp500.min]: {
                    flex: 'initial',
                  },
                },
              })}
            >
              {is_lead_gen ? (
                <>
                  {/* <Link displayAsButton buttonSize="large" href={quoteUrl}>
                        {quoteCopy}
                      </Link>
                      <Link displayAsButton buttonSize="large" variant="secondary" href={demoUrl}>
                        {demoCopy}
                      </Link> */}
                </>
              ) : (
                <SignUpCta beaconElementDetail="register" beaconElementLocation="hero">
                  {node.discovery_type?.translations?.[0]?.name
                    ? withSmartling('mktg_email_only_make_a_diagram_variable', {
                        service: node.discovery_type.translations[0].name,
                      })
                    : withSmartling('mktg_generics_sign_up_free')}
                </SignUpCta>
              )}
              {video?.external_url && (
                <Box css={{ display: 'flex' }}>
                  <Link
                    css={{ margin: 'auto' }}
                    href="#video"
                    iconAnimation="jumpLink"
                    onClick={() => setWillPlayVideo(true)}
                    variant="secondary"
                  >
                    {withSmartling('mktg_ct_consideration_video_cta')}
                  </Link>
                </Box>
              )}
            </Box>
          </Box>
          <Box
            css={theme => ({
              flex: '1',
              overflow: 'visible',
              display: 'none',
              marginLeft: theme.space.s16,
              [theme.breakpoints.bp768.min]: {
                display: 'block',
              },
            })}
          >
            {hero_image?.filename_disk && (
              <Image
                css={theme => ({
                  height: '336px',
                  position: 'absolute',
                  objectFit: 'cover',
                  [theme.breakpoints.bp900.min]: {
                    height: '750px',
                  },
                })}
                dataElementPageLocation="hero"
                imageAlt=""
                imageSrc={withImage(hero_image)}
              />
            )}
          </Box>
        </Container>
        <Container as="section" css={theme => ({ marginBottom: theme.space.s104 })} extraPadding={3}>
          <Paper
            css={theme => ({
              padding: `${theme.space.s48}px ${theme.space.s16}px ${theme.space.s104}px`,
              [theme.breakpoints.bp500.min]: {
                paddingLeft: theme.space.s32,
                paddingRight: theme.space.s32,
              },
              [theme.breakpoints.bp768.min]: {
                padding: `${theme.space.s64}px ${theme.space.s80}px ${theme.space.s104}px`,
              },
              [theme.breakpoints.bp1024.min]: {
                paddingLeft: theme.space.s104,
                paddingRight: theme.space.s104,
              },
            })}
            variant="raised"
          >
            <Heading
              as="h2"
              css={theme => ({
                width: '100%',
                marginBottom: theme.space.s64,
                [theme.breakpoints.bp900.min]: {
                  width: '70%',
                  marginBottom: theme.space.s104,
                  '&:lang(ja)': {
                    width: '100%',
                  },
                },
              })}
              variant="heading2XL"
            >
              {feature_section_heading}
            </Heading>
            <ul css={{ margin: 0 }}>
              {feature_set &&
                feature_set.length > 0 &&
                feature_set.map(({ consideration_feature_id }) => {
                  const { heading, html, image } =
                    consideration_feature_id as Lucid.Directus.Generated.Consideration_Feature;

                  return (
                    <li
                      key={heading!}
                      css={theme => ({
                        listStyleType: 'none',
                        display: 'flex',
                        flexDirection: 'column',
                        marginBottom: theme.space.s104,
                        ':last-of-type': {
                          marginBottom: 0,
                        },
                        [theme.breakpoints.bp900.min]: {
                          flexDirection: 'row',
                        },
                      })}
                    >
                      <Box
                        css={theme => ({
                          lineHeight: 0,
                          flex: '1',
                          [theme.breakpoints.bp900.min]: {
                            maxWidth: '540px',
                          },
                        })}
                      >
                        <Image
                          imageAlt={image?.translations?.[0]?.alt || ''}
                          imageSrc={withImage(image)}
                          loading="lazy"
                        />
                      </Box>
                      <Box
                        css={theme => ({
                          flex: '1',
                          marginLeft: 0,
                          marginTop: theme.space.s32,
                          [theme.breakpoints.bp900.min]: {
                            marginLeft: theme.space.s48,
                            marginTop: 0,
                          },
                        })}
                      >
                        <Heading as="h3" variant="headingM">
                          {heading}
                        </Heading>
                        <Box
                          css={theme => ({
                            hyphens: 'auto',
                            ul: {
                              listStyle: 'disc',
                            },
                            '> p': {
                              fontSize: theme.fontSizes.fs16,
                              fontWeight: theme.fontWeights.fw400,
                              lineHeight: theme.lineHeights.lh1_5,
                            },
                          })}
                        >
                          {reactHTMLParser(html)}
                        </Box>
                      </Box>
                    </li>
                  );
                })}
            </ul>
          </Paper>
        </Container>
        <Container
          as="section"
          css={theme => ({
            marginBottom: theme.space.s64,
            [theme.breakpoints.bp900.min]: {
              marginBottom: theme.space.s104,
            },
          })}
          extraPadding={3}
        >
          <Box
            css={theme => ({
              display: 'flex',
              flexDirection: 'column',
              [theme.breakpoints.bp768.min]: {
                marginLeft: theme.space.s64,
                marginRight: theme.space.s64,
              },
              [theme.breakpoints.bp1024.min]: {
                marginLeft: theme.space.s104,
                marginRight: theme.space.s104,
              },
            })}
          >
            <Heading
              as="h2"
              css={theme => ({
                margin: `0 auto ${theme.space.s64}px 0`,
                [theme.breakpoints.bp500.min]: {
                  marginLeft: theme.space.s48,
                },
                [theme.breakpoints.bp768.min]: {
                  marginLeft: theme.space.s120,
                },
              })}
              variant="heading2XL"
            >
              {step_section_heading}
            </Heading>
            <ol css={{ margin: 0 }}>
              {steps?.map(({ consideration_step_id }, i) => {
                const { heading, text } = consideration_step_id as Lucid.Directus.Generated.Consideration_Step;

                return (
                  <li
                    key={`step-number-${consideration_step_id?.id}`}
                    css={theme => ({
                      display: 'flex',
                      marginBottom: theme.space.s64,
                      '&:before': {
                        content: `'${i + 1}'`,
                        fontSize: theme.fontSizes.fs40,
                        fontWeight: theme.fontWeights.fw700,
                        lineHeight: theme.lineHeights.lh1_1,
                        paddingRight: theme.space.s32,
                        [theme.breakpoints.bp768.min]: {
                          paddingRight: theme.space.s104,
                        },
                      },
                    })}
                  >
                    <Box>
                      <Heading as="h3" variant="headingL">
                        {heading}
                      </Heading>
                      <Text
                        css={{
                          margin: 0,
                          hyphens: 'auto',
                        }}
                        variant="paragraphS400"
                      >
                        {text}
                      </Text>
                    </Box>
                  </li>
                );
              })}
            </ol>
            {step_section_link_text && (
              <SignUpCta
                beaconElementDetail="register-list"
                beaconElementLocation="body"
                css={theme => ({
                  width: 'fit-content',
                  margin: 'auto',
                  [theme.breakpoints.bp500.min]: {
                    marginLeft: theme.space.s104,
                  },
                })}
              >
                {step_section_link_text}
              </SignUpCta>
            )}
          </Box>
        </Container>
        {faqItems && faqItems.length && (
          <FAQSection
            backgroundColor={{ co: 'coolGrey.100', scale: 'earth.5', spark: 'indigo.90' }}
            css={theme => [
              video?.external_url && {
                paddingBottom: '375px',
                [theme.breakpoints.bp500.min]: {
                  paddingBottom: '425px',
                },
                [theme.breakpoints.bp900.min]: {
                  paddingBottom: '500px',
                },
              },
            ]}
            extraPadding={3}
            headingVariant="heading2XL"
            items={faqItems}
            linkVariant="secondary"
            sectionTitle={faq_section_heading}
          />
        )}
        {video?.external_url && (
          <Container
            css={theme => ({
              ':focus': {
                outline: 'none',
              },
              marginTop: '-250px',
              marginBottom: theme.space.s64,
              [theme.breakpoints.bp500.min]: {
                marginTop: '-300px',
                marginBottom: theme.space.s104,
              },
              [theme.breakpoints.bp900.min]: {
                marginTop: '-375px',
              },
            })}
            extraPadding={3}
            id="video"
          >
            <Heading
              as="h2"
              css={theme => ({
                color: theme.colors.white,
                marginBottom: theme.space.s40,
                [theme.breakpoints.bp500.min]: {
                  marginBottom: theme.space.s64,
                },
              })}
              variant="heading2XL"
            >
              {video.translations?.[0]?.title || video.title}
            </Heading>
            <VideoPlayer
              controls
              description={video.translations?.[0]?.alt_description || video.alt_text}
              descriptionElementId="consideration-video-description"
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onClick={() =>
                sendBeaconEvent({
                  name: 'clickedMarketingPageElement',
                  elementLocation: 'body',
                  elementDetail: 'video',
                  elementType: 'video-button',
                })
              }
              placeholder={() => (
                <Image
                  imageAlt={video.preview_image?.translations?.[0]?.alt || ''}
                  imageSrc={withImage(video.preview_image)}
                  loading="lazy"
                />
              )}
              playing={willPlayVideo}
              url={video.external_url}
            />
          </Container>
        )}
        <Box
          as="section"
          css={theme => ({
            backgroundColor: theme.colors.white,
          })}
        >
          <TemplateSection
            cards={templateCards}
            css={{ paddingBottom: 0 }}
            heading={template_section_heading}
            link={template_section_link}
            shouldUseGrid={shouldRequireTemplateGrid}
            text={template_section_text}
          />
          {!pre_footer_heading && product === 'spark' ? (
            <SimpleCta
              beaconEvent={{
                name: 'clickedMarketingPageElement',
                elementLocation: 'prefooter',
                elementDetail: 'b2b',
                elementType: 'button-primary',
              }}
              css={theme => ({
                [theme.breakpoints.bp1440.min]: {
                  paddingRight: '3.5rem',
                  paddingLeft: '3.5rem',
                },
              })}
              cta={{
                id: 'simple-cta-link',
                default_link_text: withSmartling('mktg_learn_more'),
                final_url: footerLink,
              }}
              heading={withSmartling('mktg_consideration_see_why_spark')}
            />
          ) : pre_footer_heading ? (
            // spark uses SimpleCta instead of PreFooterCta
            <PreFooterCta
              backgroundColor={{ co: 'blue.60', scale: 'indigo.60', spark: 'red.60' }}
              ctas={[
                {
                  final_url: footerLink,
                  default_link_text: withSmartling('mktg_nav_start_free_trial'),
                } as Lucid.Directus.Generated.Links,
              ]}
              heading={pre_footer_heading}
            />
          ) : (
            <></>
          )}
        </Box>
      </Box>
    </Layout>
  );
};

export default Consideration;

export const Head: HeadFC<Lucid.Context.HeadData, Lucid.Context.HeadContext> = ({ ...props }) => (
  <LucidHead {...props} />
);

export const query = graphql`
  query ConsiderationQuery($nid: ID!, $shortCode: String!) {
    directus {
      node: consideration_by_id(id: $nid) {
        nodeTranslations: translations {
          path {
            url
          }
          languages_code {
            short_code
          }
          is_published
        }
        discovery_type {
          translations(filter: { languages_code: { short_code: { _eq: $shortCode } } }) {
            name
          }
        }
        hero_image {
          ...mediaFragment
        }
        is_lead_gen
        translations(filter: { languages_code: { short_code: { _eq: $shortCode } } }) {
          heading
          feature_section_heading
          feature_set {
            consideration_feature_id {
              id
              heading
              html
              image {
                ...mediaFragment
              }
            }
          }
          step_section_heading
          steps {
            consideration_step_id {
              id
              heading
              text
            }
          }
          step_section_link_text
          faq_section_heading
          faqs {
            faq_id {
              id
              question
              answer
              links {
                links_id {
                  id
                  default_link_text
                  final_url
                  beacon_id
                }
              }
            }
          }
          video {
            title
            alt_text
            duration_min
            duration_sec
            date
            external_url
            preview_image {
              ...mediaFragment
            }
            translations(filter: { languages_code: { short_code: { _eq: $shortCode } } }) {
              title
              alt_description
            }
          }
          quote_text
          should_require_template_grid
          template_section_heading
          template_section_text
          template_section_link {
            id
            default_link_text
            final_url
          }
          template_cards {
            template_card_id {
              id
              heading
              template_id
              image {
                ...mediaFragment
              }
            }
          }
          pre_footer_heading
        }
      }
    }
  }
`;
